import React, { useState, useEffect } from "react";
import config from "../../config/config";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const ContentManager = () => {
  const [activeSection, setActiveSection] = useState("homepage");
  const [loading, setLoading] = useState(false);

  // Homepage content state
  const [homepageContent, setHomepageContent] = useState({
    hero_title: "",
    hero_subtitle: "",
    hero_description: "",
    products_title: "",
    products_subtitle: "",
    about_title: "",
    about_description: "",
    services_title: "",
    services_subtitle: "",
    cta_title: "",
    cta_description: "",
  });

  // About Us content state
  const [aboutContent, setAboutContent] = useState({
    title: "",
    subtitle: "",
    description: "",
    mission: "",
    vision: "",
    values: "",
    team_title: "",
    team_description: "",
  });

  // Career openings state
  const [careerOpenings, setCareerOpenings] = useState([]);
  const [showJobModal, setShowJobModal] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [jobFormData, setJobFormData] = useState({
    title: "",
    department: "",
    location: "",
    type: "full-time",
    experience: "",
    salary_range: "",
    description: "",
    requirements: "",
    benefits: "",
    is_active: true,
  });

  // Contact Us content state
  const [contactContent, setContactContent] = useState({
    title: "",
    subtitle: "",
    address: "",
    phone: "",
    email: "",
    office_hours: "",
    map_embed: "",
  });

  // Team members state
  const [teamMembers, setTeamMembers] = useState([]);
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [editingTeamMember, setEditingTeamMember] = useState(null);
  const [teamFormData, setTeamFormData] = useState({
    name: "",
    position: "",
    description: "",
    image_url: "",
    email: "",
    linkedin_url: "",
  });

  // Infrastructure state
  const [infrastructure, setInfrastructure] = useState([]);
  const [showInfrastructureModal, setShowInfrastructureModal] = useState(false);
  const [editingInfrastructure, setEditingInfrastructure] = useState(null);
  const [infrastructureFormData, setInfrastructureFormData] = useState({
    title: "",
    description: "",
    icon: "",
    image_url: "",
  });

  // Certifications state
  const [certifications, setCertifications] = useState([]);
  const [showCertificationModal, setShowCertificationModal] = useState(false);
  const [editingCertification, setEditingCertification] = useState(null);
  const [certificationFormData, setCertificationFormData] = useState({
    title: "",
    description: "",
    icon: "",
    image_url: "",
    cert_number: "",
    issued_date: "",
    expiry_date: "",
    issuing_body: "",
  });

  // Services state
  const [services, setServices] = useState([]);
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [serviceFormData, setServiceFormData] = useState({
    title: "",
    description: "",
    icon: "",
    image_url: "",
    link_url: "",
    is_active: true,
  });

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Fetch content on component mount and section change
  useEffect(() => {
    fetchContent();
  }, [activeSection]);

  // Fetch content based on active section
  const fetchContent = async () => {
    try {
      setLoading(true);
      

      let endpoint = "";
      switch (activeSection) {
        case "homepage":
          endpoint = "get_homepage_content.php";
          break;
        case "about":
          endpoint = "get_about_content.php";
          break;
        case "team":
          endpoint = "get_team_members.php";
          break;
        case "infrastructure":
          endpoint = "get_infrastructure.php";
          break;
        case "certifications":
          endpoint = "get_certifications.php";
          break;
        case "services":
          endpoint = "get_services.php";
          break;
        case "careers":
          endpoint = "get_career_openings.php";
          break;
        case "contact":
          endpoint = "get_contact_content.php";
          break;
      }

      const response = await fetch(config.getAdminApiUrl(endpoint), {
        headers: {
          "X-API-Key": "ncg_admin_2024",
        },
      });
      const data = await response.json();

      if (data.success) {
        switch (activeSection) {
          case "homepage":
            // Map nested content to flat structure
            const homepageData = {
              hero_title: data.content.hero?.title || "",
              hero_subtitle: data.content.hero?.subtitle || "",
              hero_description: data.content.hero?.description || "",
              products_title: data.content.products?.title || "",
              products_subtitle: data.content.products?.subtitle || "",
              products_description: data.content.products?.description || "",
              about_title: data.content.about?.title || "",
              about_subtitle: data.content.about?.subtitle || "",
              about_description: data.content.about?.description || "",
              services_title: data.content.services?.title || "",
              services_subtitle: data.content.services?.subtitle || "",
              services_description: data.content.services?.description || "",
              cta_title: data.content.cta?.title || "",
              cta_subtitle: data.content.cta?.subtitle || "",
              cta_description: data.content.cta?.description || "",
            };
            setHomepageContent(homepageData);
            break;
          case "about":
            // Map nested content to flat structure
            const aboutData = {
              title: data.content.main?.title || "",
              subtitle: data.content.main?.subtitle || "",
              description: data.content.main?.description || "",
              mission: data.content.main?.mission || "",
              vision: data.content.main?.vision || "",
              values: data.content.main?.values_text || "",
              team_title: data.content.main?.team_title || "",
              team_description: data.content.main?.team_description || "",
            };
            setAboutContent(aboutData);

            // Also fetch team, infrastructure, and certifications data
            try {
              // Fetch team members
              const teamResponse = await fetch(
                config.getAdminApiUrl("get_team_members.php"),
                {
                  headers: { "X-API-Key": "ncg_admin_2024" },
                }
              );
              const teamData = await teamResponse.json();
              if (teamData.success) {
                setTeamMembers(teamData.team_members || []);
              }

              // Fetch infrastructure
              const infrastructureResponse = await fetch(
                config.getAdminApiUrl("get_infrastructure.php"),
                {
                  headers: { "X-API-Key": "ncg_admin_2024" },
                }
              );
              const infrastructureData = await infrastructureResponse.json();
              if (infrastructureData.success) {
                setInfrastructure(infrastructureData.infrastructure || []);
              }

              // Fetch certifications
              const certificationsResponse = await fetch(
                config.getAdminApiUrl("get_certifications.php"),
                {
                  headers: { "X-API-Key": "ncg_admin_2024" },
                }
              );
              const certificationsData = await certificationsResponse.json();
              if (certificationsData.success) {
                setCertifications(certificationsData.certifications || []);
              }
            } catch (error) {
              console.error("Error fetching additional data:", error);
            }
            break;
          case "services":
            setServices(Array.isArray(data.services) ? data.services : []);
            break;
          case "careers":
            setCareerOpenings(data.careers);
            break;
          case "contact":
            setContactContent(data.content);
            break;
        }
      }
    } catch (error) {
      console.error("Error fetching content:", error);
    } finally {
      setLoading(false);
    }
  };

  // Handle content updates
  const handleContentUpdate = async (contentType, formData) => {
    try {
      setLoading(true);
      

      let endpoint = "";
      let structuredData = {};

      switch (contentType) {
        case "homepage":
          endpoint = "update_homepage_content.php";
          // Structure data for homepage sections
          structuredData = {
            hero: {
              title: formData.hero_title,
              subtitle: formData.hero_subtitle,
              description: formData.hero_description,
            },
            products: {
              title: formData.products_title,
              subtitle: formData.products_subtitle,
              description: formData.products_description,
            },
            about: {
              title: formData.about_title,
              subtitle: formData.about_subtitle,
              description: formData.about_description,
            },
            services: {
              title: formData.services_title,
              subtitle: formData.services_subtitle,
              description: formData.services_description,
            },
            cta: {
              title: formData.cta_title,
              subtitle: formData.cta_subtitle,
              description: formData.cta_description,
            },
          };
          break;
        case "about":
          endpoint = "update_about_content.php";
          // Structure data for about section
          structuredData = {
            main: {
              title: formData.title,
              subtitle: formData.subtitle,
              description: formData.description,
              mission: formData.mission,
              vision: formData.vision,
              values_text: formData.values,
              team_title: formData.team_title,
              team_description: formData.team_description,
            },
          };
          break;
        case "contact":
          endpoint = "update_contact_content.php";
          structuredData = formData;
          break;
      }

      const response = await fetch(config.getAdminApiUrl(endpoint), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": "ncg_admin_2024",
        },
        body: JSON.stringify(structuredData),
      });

      const data = await response.json();

      if (data.success) {
        alert("Content updated successfully!");
        fetchContent();
      } else {
        alert("Error updating content: " + data.message);
      }
    } catch (error) {
      console.error("Error updating content:", error);
      alert("Error updating content");
    } finally {
      setLoading(false);
    }
  };

  // Handle job creation/update
  const handleJobSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);
      

      const url = editingJob
        ? config.getAdminApiUrl("update_career_opening.php")
        : config.getAdminApiUrl("create_career_opening.php");

      const requestData = editingJob
        ? { ...jobFormData, id: editingJob.id }
        : jobFormData;

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": "ncg_admin_2024",
        },
        body: JSON.stringify(requestData),
      });

      const data = await response.json();

      if (data.success) {
        fetchContent();
        resetJobForm();
        setShowJobModal(false);
        alert(
          editingJob ? "Job updated successfully!" : "Job created successfully!"
        );
      } else {
        alert("Error: " + data.message);
      }
    } catch (error) {
      console.error("Error saving job:", error);
      alert("Error saving job");
    } finally {
      setLoading(false);
    }
  };

  // Handle job deletion
  const handleJobDelete = async (jobId) => {
    if (!window.confirm("Are you sure you want to delete this job opening?"))
      return;

    try {
      setLoading(true);
      

      const response = await fetch(config.getAdminApiUrl("delete_career_opening.php"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": "ncg_admin_2024",
        },
        body: JSON.stringify({ id: jobId }),
      });

      const data = await response.json();

      if (data.success) {
        fetchContent();
        alert("Job opening deleted successfully!");
      } else {
        alert("Error deleting job: " + data.message);
      }
    } catch (error) {
      console.error("Error deleting job:", error);
      alert("Error deleting job");
    } finally {
      setLoading(false);
    }
  };

  // Reset job form
  const resetJobForm = () => {
    setJobFormData({
      title: "",
      department: "",
      location: "",
      type: "full-time",
      experience: "",
      salary_range: "",
      description: "",
      requirements: "",
      benefits: "",
      is_active: true,
    });
    setEditingJob(null);
  };

  // Handle job edit
  const handleJobEdit = (job) => {
    setJobFormData({
      title: job.title,
      department: job.department,
      location: job.location,
      type: job.type,
      experience: job.experience,
      salary_range: job.salary_range,
      description: job.description,
      requirements: job.requirements,
      benefits: job.benefits,
      is_active: job.is_active,
    });
    setEditingJob(job);
    setShowJobModal(true);
  };

  // Handle team member creation/update
  const handleTeamMemberSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);
      

      const url = editingTeamMember
        ? config.getAdminApiUrl("update_team_member.php")
        : config.getAdminApiUrl("create_team_member.php");

      const requestData = editingTeamMember
        ? { ...teamFormData, id: editingTeamMember.id }
        : teamFormData;

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": "ncg_admin_2024",
        },
        body: JSON.stringify(requestData),
      });

      const data = await response.json();

      if (data.success) {
        fetchContent();
        resetTeamForm();
        setShowTeamModal(false);
        alert(
          editingTeamMember
            ? "Team member updated successfully!"
            : "Team member created successfully!"
        );
      } else {
        alert("Error: " + data.message);
      }
    } catch (error) {
      console.error("Error saving team member:", error);
      alert("Error saving team member");
    } finally {
      setLoading(false);
    }
  };

  // Handle team member deletion
  const handleTeamMemberDelete = async (memberId) => {
    if (!window.confirm("Are you sure you want to delete this team member?"))
      return;

    try {
      setLoading(true);
      

      const response = await fetch(config.getAdminApiUrl("delete_team_member.php"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": "ncg_admin_2024",
        },
        body: JSON.stringify({ id: memberId }),
      });

      const data = await response.json();

      if (data.success) {
        fetchContent();
        alert("Team member deleted successfully!");
      } else {
        alert("Error deleting team member: " + data.message);
      }
    } catch (error) {
      console.error("Error deleting team member:", error);
      alert("Error deleting team member");
    } finally {
      setLoading(false);
    }
  };

  // Reset team form
  const resetTeamForm = () => {
    setTeamFormData({
      name: "",
      position: "",
      description: "",
      image_url: "",
      email: "",
      linkedin_url: "",
    });
    setEditingTeamMember(null);
  };

  // Handle team member edit
  const handleTeamMemberEdit = (member) => {
    setTeamFormData({
      name: member.name || "",
      position: member.position || "",
      description: member.description || "",
      image_url: member.image_url || "",
      email: member.email || "",
      linkedin_url: member.linkedin_url || "",
    });
    setEditingTeamMember(member);
    setShowTeamModal(true);
  };

  // Handle infrastructure creation/update
  const handleInfrastructureSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);
      

      const url = editingInfrastructure
        ? config.getAdminApiUrl("update_infrastructure_item.php")
        : config.getAdminApiUrl("create_infrastructure_item.php");

      const requestData = editingInfrastructure
        ? { ...infrastructureFormData, id: editingInfrastructure.id }
        : infrastructureFormData;

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": "ncg_admin_2024",
        },
        body: JSON.stringify(requestData),
      });

      const data = await response.json();

      if (data.success) {
        fetchContent();
        resetInfrastructureForm();
        setShowInfrastructureModal(false);
        alert(
          editingInfrastructure
            ? "Infrastructure item updated successfully!"
            : "Infrastructure item created successfully!"
        );
      } else {
        alert("Error: " + data.message);
      }
    } catch (error) {
      console.error("Error saving infrastructure item:", error);
      alert("Error saving infrastructure item");
    } finally {
      setLoading(false);
    }
  };

  // Handle infrastructure deletion
  const handleInfrastructureDelete = async (itemId) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this infrastructure item?"
      )
    )
      return;

    try {
      setLoading(true);
      

      const response = await fetch(
        config.getAdminApiUrl("delete_infrastructure_item.php"),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-API-Key": "ncg_admin_2024",
          },
          body: JSON.stringify({ id: itemId }),
        }
      );

      const data = await response.json();

      if (data.success) {
        fetchContent();
        alert("Infrastructure item deleted successfully!");
      } else {
        alert("Error deleting infrastructure item: " + data.message);
      }
    } catch (error) {
      console.error("Error deleting infrastructure item:", error);
      alert("Error deleting infrastructure item");
    } finally {
      setLoading(false);
    }
  };

  // Reset infrastructure form
  const resetInfrastructureForm = () => {
    setInfrastructureFormData({
      title: "",
      description: "",
      icon: "",
      image_url: "",
    });
    setEditingInfrastructure(null);
  };

  // Handle infrastructure edit
  const handleInfrastructureEdit = (item) => {
    setInfrastructureFormData({
      title: item.title || "",
      description: item.description || "",
      icon: item.icon || "",
      image_url: item.image_url || "",
    });
    setEditingInfrastructure(item);
    setShowInfrastructureModal(true);
  };

  // Handle certification creation/update
  const handleCertificationSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);
      

      const url = editingCertification
        ? config.getAdminApiUrl("update_certification.php")
        : config.getAdminApiUrl("create_certification.php");

      const requestData = editingCertification
        ? { ...certificationFormData, id: editingCertification.id }
        : certificationFormData;

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": "ncg_admin_2024",
        },
        body: JSON.stringify(requestData),
      });

      const data = await response.json();

      if (data.success) {
        fetchContent();
        resetCertificationForm();
        setShowCertificationModal(false);
        alert(
          editingCertification
            ? "Certification updated successfully!"
            : "Certification created successfully!"
        );
      } else {
        alert("Error: " + data.message);
      }
    } catch (error) {
      console.error("Error saving certification:", error);
      alert("Error saving certification");
    } finally {
      setLoading(false);
    }
  };

  // Handle certification deletion
  const handleCertificationDelete = async (certId) => {
    if (!window.confirm("Are you sure you want to delete this certification?"))
      return;

    try {
      setLoading(true);
      

      const response = await fetch(config.getAdminApiUrl("delete_certification.php"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": "ncg_admin_2024",
        },
        body: JSON.stringify({ id: certId }),
      });

      const data = await response.json();

      if (data.success) {
        fetchContent();
        alert("Certification deleted successfully!");
      } else {
        alert("Error deleting certification: " + data.message);
      }
    } catch (error) {
      console.error("Error deleting certification:", error);
      alert("Error deleting certification");
    } finally {
      setLoading(false);
    }
  };

  // Reset certification form
  const resetCertificationForm = () => {
    setCertificationFormData({
      title: "",
      description: "",
      icon: "",
      image_url: "",
      cert_number: "",
      issued_date: "",
      expiry_date: "",
      issuing_body: "",
    });
    setEditingCertification(null);
  };

  // Handle certification edit
  const handleCertificationEdit = (cert) => {
    setCertificationFormData({
      title: cert.title || "",
      description: cert.description || "",
      icon: cert.icon || "",
      image_url: cert.image_url || "",
      cert_number: cert.cert_number || "",
      issued_date: cert.issued_date || "",
      expiry_date: cert.expiry_date || "",
      issuing_body: cert.issuing_body || "",
    });
    setEditingCertification(cert);
    setShowCertificationModal(true);
  };

  // Handle service creation/update
  const handleServiceSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);
      

      const url = editingService
        ? config.getAdminApiUrl("update_service.php")
        : config.getAdminApiUrl("create_service.php");

      const requestData = editingService
        ? { ...serviceFormData, id: editingService.id }
        : serviceFormData;

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": "ncg_admin_2024",
        },
        body: JSON.stringify(requestData),
      });

      const data = await response.json();

      if (data.success) {
        fetchContent();
        resetServiceForm();
        setShowServiceModal(false);
        alert(
          editingService
            ? "Service updated successfully!"
            : "Service created successfully!"
        );
      } else {
        alert("Error: " + data.message);
      }
    } catch (error) {
      console.error("Error saving service:", error);
      alert("Error saving service");
    } finally {
      setLoading(false);
    }
  };

  // Handle service deletion
  const handleServiceDelete = async (serviceId) => {
    if (!window.confirm("Are you sure you want to delete this service?"))
      return;

    try {
      setLoading(true);
      

      const response = await fetch(config.getAdminApiUrl("delete_service.php"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": "ncg_admin_2024",
        },
        body: JSON.stringify({ id: serviceId }),
      });

      const data = await response.json();

      if (data.success) {
        fetchContent();
        alert("Service deleted successfully!");
      } else {
        alert("Error deleting service: " + data.message);
      }
    } catch (error) {
      console.error("Error deleting service:", error);
      alert("Error deleting service");
    } finally {
      setLoading(false);
    }
  };

  // Reset service form
  const resetServiceForm = () => {
    setServiceFormData({
      title: "",
      description: "",
      icon: "",
      image_url: "",
      link_url: "",
      is_active: true,
    });
    setEditingService(null);
  };

  // Handle service edit
  const handleServiceEdit = (service) => {
    setServiceFormData({
      title: service.title || "",
      description: service.description || "",
      icon: service.icon || "",
      image_url: service.image_url || "",
      link_url: service.link_url || "",
      is_active: service.is_active,
    });
    setEditingService(service);
    setShowServiceModal(true);
  };

  // Handle drag and drop for services
  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active.id !== over.id && services && services.length > 0) {
      const oldIndex = services.findIndex(
        (service) => service.id === active.id
      );
      const newIndex = services.findIndex((service) => service.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const newServices = arrayMove(services, oldIndex, newIndex);
        setServices(newServices);

        try {
          

          const response = await fetch(
            config.getAdminApiUrl("update_service_order.php"),
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "X-API-Key": "ncg_admin_2024",
              },
              body: JSON.stringify({ services: newServices }),
            }
          );

          const data = await response.json();
          if (!data.success) {
            alert("Error updating service order: " + data.message);
          }
        } catch (error) {
          console.error("Error updating service order:", error);
          alert("Error updating service order");
        }
      }
    }
  };

  // Navigation items
  const sections = [
    { id: "homepage", label: "Homepage Content", icon: "fas fa-home" },
    { id: "about", label: "About Us Content", icon: "fas fa-info-circle" },
    { id: "services", label: "Services Management", icon: "fas fa-cogs" },
    { id: "careers", label: "Career Openings", icon: "fas fa-briefcase" },
    { id: "contact", label: "Contact Us Content", icon: "fas fa-envelope" },
  ];

  // Render content based on active section
  const renderSectionContent = () => {
    switch (activeSection) {
      case "homepage":
        return renderHomepageContent();
      case "about":
        return renderAboutContent();
      case "services":
        return renderServicesContent();
      case "careers":
        return renderCareersContent();
      case "contact":
        return renderContactContent();
      default:
        return null;
    }
  };

  // Render homepage content form
  const renderHomepageContent = () => (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        handleContentUpdate("homepage", homepageContent);
      }}
      className="content-form"
    >
      <div className="form-section">
        <h3>Hero Section</h3>
        <div className="form-grid">
          <div className="form-group">
            <label>Hero Title</label>
            <input
              type="text"
              value={homepageContent.hero_title}
              onChange={(e) =>
                setHomepageContent({
                  ...homepageContent,
                  hero_title: e.target.value,
                })
              }
              placeholder="Main hero title"
            />
          </div>
          <div className="form-group">
            <label>Hero Subtitle</label>
            <input
              type="text"
              value={homepageContent.hero_subtitle}
              onChange={(e) =>
                setHomepageContent({
                  ...homepageContent,
                  hero_subtitle: e.target.value,
                })
              }
              placeholder="Hero subtitle"
            />
          </div>
          <div className="form-group full-width">
            <label>Hero Description</label>
            <textarea
              value={homepageContent.hero_description}
              onChange={(e) =>
                setHomepageContent({
                  ...homepageContent,
                  hero_description: e.target.value,
                })
              }
              rows="3"
              placeholder="Hero section description"
            />
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3>Products Section</h3>
        <div className="form-grid">
          <div className="form-group">
            <label>Products Title</label>
            <input
              type="text"
              value={homepageContent.products_title}
              onChange={(e) =>
                setHomepageContent({
                  ...homepageContent,
                  products_title: e.target.value,
                })
              }
              placeholder="Products section title"
            />
          </div>
          <div className="form-group">
            <label>Products Subtitle</label>
            <input
              type="text"
              value={homepageContent.products_subtitle}
              onChange={(e) =>
                setHomepageContent({
                  ...homepageContent,
                  products_subtitle: e.target.value,
                })
              }
              placeholder="Products section subtitle"
            />
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3>About Section</h3>
        <div className="form-grid">
          <div className="form-group">
            <label>About Title</label>
            <input
              type="text"
              value={homepageContent.about_title}
              onChange={(e) =>
                setHomepageContent({
                  ...homepageContent,
                  about_title: e.target.value,
                })
              }
              placeholder="About section title"
            />
          </div>
          <div className="form-group full-width">
            <label>About Description</label>
            <textarea
              value={homepageContent.about_description}
              onChange={(e) =>
                setHomepageContent({
                  ...homepageContent,
                  about_description: e.target.value,
                })
              }
              rows="4"
              placeholder="About section description"
            />
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3>CTA Section</h3>
        <div className="form-grid">
          <div className="form-group">
            <label>CTA Title</label>
            <input
              type="text"
              value={homepageContent.cta_title}
              onChange={(e) =>
                setHomepageContent({
                  ...homepageContent,
                  cta_title: e.target.value,
                })
              }
              placeholder="Call to action title"
            />
          </div>
          <div className="form-group full-width">
            <label>CTA Description</label>
            <textarea
              value={homepageContent.cta_description}
              onChange={(e) =>
                setHomepageContent({
                  ...homepageContent,
                  cta_description: e.target.value,
                })
              }
              rows="3"
              placeholder="Call to action description"
            />
          </div>
        </div>
      </div>

      <button type="submit" className="btn btn-primary" disabled={loading}>
        {loading ? (
          <i className="fas fa-spinner fa-spin me-2"></i>
        ) : (
          <i className="fas fa-save me-2"></i>
        )}
        Update Homepage Content
      </button>
    </form>
  );

  // Render about content form
  const renderAboutContent = () => (
    <div className="about-content">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleContentUpdate("about", aboutContent);
        }}
        className="content-form"
      >
        <div className="form-section">
          <h3>About Us Content</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>Title</label>
              <input
                type="text"
                value={aboutContent.title}
                onChange={(e) =>
                  setAboutContent({ ...aboutContent, title: e.target.value })
                }
                placeholder="About us title"
              />
            </div>
            <div className="form-group">
              <label>Subtitle</label>
              <input
                type="text"
                value={aboutContent.subtitle}
                onChange={(e) =>
                  setAboutContent({ ...aboutContent, subtitle: e.target.value })
                }
                placeholder="About us subtitle"
              />
            </div>
            <div className="form-group full-width">
              <label>Description</label>
              <textarea
                value={aboutContent.description}
                onChange={(e) =>
                  setAboutContent({
                    ...aboutContent,
                    description: e.target.value,
                  })
                }
                rows="5"
                placeholder="Main about us description"
              />
            </div>
            <div className="form-group full-width">
              <label>Mission</label>
              <textarea
                value={aboutContent.mission}
                onChange={(e) =>
                  setAboutContent({ ...aboutContent, mission: e.target.value })
                }
                rows="3"
                placeholder="Company mission statement"
              />
            </div>
            <div className="form-group full-width">
              <label>Vision</label>
              <textarea
                value={aboutContent.vision}
                onChange={(e) =>
                  setAboutContent({ ...aboutContent, vision: e.target.value })
                }
                rows="3"
                placeholder="Company vision statement"
              />
            </div>
            <div className="form-group full-width">
              <label>Values</label>
              <textarea
                value={aboutContent.values}
                onChange={(e) =>
                  setAboutContent({ ...aboutContent, values: e.target.value })
                }
                rows="3"
                placeholder="Company values"
              />
            </div>
          </div>
        </div>

        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? (
            <i className="fas fa-spinner fa-spin me-2"></i>
          ) : (
            <i className="fas fa-save me-2"></i>
          )}
          Update About Content
        </button>
      </form>

      {/* Leadership Team Section */}
      <div className="content-subsection team-section">
        <div className="section-header">
          <h4>Leadership Team</h4>
          <button
            className="btn btn-primary"
            onClick={() => {
              resetTeamForm();
              setShowTeamModal(true);
            }}
          >
            <i className="fas fa-plus me-2"></i>
            Add Team Member
          </button>
        </div>

        {loading ? (
          <div className="loading-spinner">
            <i className="fas fa-spinner fa-spin"></i> Loading...
          </div>
        ) : (
          <div className="team-table-container">
            {teamMembers.length > 0 ? (
              teamMembers.map((member) => (
                <div key={member.id} className="team-member-item">
                  <div className="member-info">
                    <h5>{member.name}</h5>
                    <p>
                      <strong>Position:</strong> {member.position}
                    </p>
                    <p>
                      <strong>Description:</strong> {member.description}
                    </p>
                    <p>
                      <strong>Email:</strong>{" "}
                      {member.email ? (
                        <a href={`mailto:${member.email}`}>{member.email}</a>
                      ) : (
                        "-"
                      )}
                    </p>
                    <p>
                      <strong>LinkedIn:</strong>{" "}
                      {member.linkedin_url ? (
                        <a
                          href={member.linkedin_url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <i className="fab fa-linkedin"></i> View Profile
                        </a>
                      ) : (
                        "-"
                      )}
                    </p>
                  </div>
                  <div className="member-actions">
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={() => handleTeamMemberEdit(member)}
                      title="Edit Team Member"
                    >
                      <i className="fas fa-edit"></i> Edit
                    </button>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => handleTeamMemberDelete(member.id)}
                      title="Delete Team Member"
                    >
                      <i className="fas fa-trash"></i> Delete
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="no-data">
                <i className="fas fa-users"></i>
                <p>
                  No team members found. Click "Add Team Member" to get started.
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Infrastructure Section */}
      <div className="content-subsection infrastructure-section">
        <div className="section-header">
          <h4>Our Infrastructure</h4>
          <button
            className="btn btn-primary"
            onClick={() => {
              resetInfrastructureForm();
              setShowInfrastructureModal(true);
            }}
          >
            <i className="fas fa-plus me-2"></i>
            Add Infrastructure Item
          </button>
        </div>

        {loading ? (
          <div className="loading-spinner">
            <i className="fas fa-spinner fa-spin"></i> Loading...
          </div>
        ) : (
          <div className="infrastructure-table-container">
            {infrastructure.length > 0 ? (
              infrastructure.map((item) => (
                <div key={item.id} className="infrastructure-item">
                  <div className="item-info">
                    <h5>{item.title}</h5>
                    <p>
                      <strong>Description:</strong> {item.description}
                    </p>
                    <p>
                      <strong>Icon:</strong>{" "}
                      {item.icon ? (
                        <span className="icon-badge">
                          <i className={item.icon}></i> {item.icon}
                        </span>
                      ) : (
                        "-"
                      )}
                    </p>
                  </div>
                  <div className="item-actions">
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={() => handleInfrastructureEdit(item)}
                      title="Edit Infrastructure Item"
                    >
                      <i className="fas fa-edit"></i> Edit
                    </button>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => handleInfrastructureDelete(item.id)}
                      title="Delete Infrastructure Item"
                    >
                      <i className="fas fa-trash"></i> Delete
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="no-data">
                <i className="fas fa-industry"></i>
                <p>No infrastructure items found</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Certifications Section */}
      <div className="content-subsection certifications-section">
        <div className="section-header">
          <h4>Certifications & Standards</h4>
          <button
            className="btn btn-primary"
            onClick={() => {
              resetCertificationForm();
              setShowCertificationModal(true);
            }}
          >
            <i className="fas fa-plus me-2"></i>
            Add Certification
          </button>
        </div>

        {loading ? (
          <div className="loading-spinner">
            <i className="fas fa-spinner fa-spin"></i> Loading...
          </div>
        ) : (
          <div className="certifications-table-container">
            {certifications.length > 0 ? (
              certifications.map((cert) => (
                <div key={cert.id} className="certification-item">
                  <div className="cert-info">
                    <h5>{cert.title}</h5>
                    <p>
                      <strong>Description:</strong> {cert.description}
                    </p>
                    <p>
                      <strong>Certificate Number:</strong>{" "}
                      {cert.certificate_number || "-"}
                    </p>
                    <p>
                      <strong>Issuing Body:</strong> {cert.issuing_body || "-"}
                    </p>
                    <p>
                      <strong>Issued Date:</strong> {cert.issued_date || "-"}
                    </p>
                    <p>
                      <strong>Expiry Date:</strong> {cert.expiry_date || "-"}
                    </p>
                  </div>
                  <div className="cert-actions">
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={() => handleCertificationEdit(cert)}
                      title="Edit Certification"
                    >
                      <i className="fas fa-edit"></i> Edit
                    </button>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => handleCertificationDelete(cert.id)}
                      title="Delete Certification"
                    >
                      <i className="fas fa-trash"></i> Delete
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="no-data">
                <i className="fas fa-certificate"></i>
                <p>No certifications found</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  // Render careers content
  const renderCareersContent = () => (
    <div className="careers-content">
      <div className="section-header">
        <h3>Career Openings</h3>
        <button
          className="btn btn-primary"
          onClick={() => {
            resetJobForm();
            setShowJobModal(true);
          }}
        >
          <i className="fas fa-plus me-2"></i>
          Add Job Opening
        </button>
      </div>

      {loading ? (
        <div className="loading-spinner">
          <i className="fas fa-spinner fa-spin"></i> Loading...
        </div>
      ) : (
        <div className="jobs-grid">
          {careerOpenings.length > 0 ? (
            careerOpenings.map((job) => (
              <div key={job.id} className="job-card">
                <div className="job-header">
                  <h4>{job.title}</h4>
                  <span
                    className={`job-status ${
                      job.is_active ? "active" : "inactive"
                    }`}
                  >
                    {job.is_active ? "Active" : "Inactive"}
                  </span>
                </div>
                <div className="job-details">
                  <p>
                    <strong>Department:</strong> {job.department}
                  </p>
                  <p>
                    <strong>Location:</strong> {job.location}
                  </p>
                  <p>
                    <strong>Type:</strong> {job.type}
                  </p>
                  {job.experience && (
                    <p>
                      <strong>Experience:</strong> {job.experience}
                    </p>
                  )}
                  {job.salary_range && (
                    <p>
                      <strong>Salary:</strong> {job.salary_range}
                    </p>
                  )}
                </div>
                <div className="job-actions">
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => handleJobEdit(job)}
                  >
                    <i className="fas fa-edit"></i> Edit
                  </button>
                  <button
                    className="btn btn-sm btn-danger"
                    onClick={() => handleJobDelete(job.id)}
                  >
                    <i className="fas fa-trash"></i> Delete
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div className="no-data">No job openings found</div>
          )}
        </div>
      )}
    </div>
  );

  // Sortable Service Item Component
  const SortableServiceItem = ({ service, index }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: service.id });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
    };

    return (
      <div
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...listeners}
        className={`service-item ${isDragging ? "dragging" : ""}`}
      >
        <div className="service-info">
          <div className="service-header">
            <span className="service-order">#{index + 1}</span>
            <h5>{service.title}</h5>
            <span
              className={`service-status ${
                service.is_active ? "active" : "inactive"
              }`}
            >
              {service.is_active ? "Active" : "Inactive"}
            </span>
          </div>
          <p className="service-description">{service.description}</p>
          <div className="service-details">
            <span className="service-icon">
              <i className={service.icon}></i> {service.icon}
            </span>
            {service.link_url && (
              <span className="service-link">
                <a
                  href={service.link_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className="fas fa-external-link-alt"></i> View Link
                </a>
              </span>
            )}
          </div>
        </div>
        <div className="service-actions">
          <button
            className="btn btn-sm btn-secondary"
            onClick={() => handleServiceEdit(service)}
            title="Edit Service"
          >
            <i className="fas fa-edit"></i> Edit
          </button>
          <button
            className="btn btn-sm btn-danger"
            onClick={() => handleServiceDelete(service.id)}
            title="Delete Service"
          >
            <i className="fas fa-trash"></i> Delete
          </button>
        </div>
      </div>
    );
  };

  // Render services content
  const renderServicesContent = () => (
    <div className="services-content">
      <div className="section-header">
        <h3>Services Management</h3>
        <div className="section-info">
          <p>
            Manage your services. Only the top 5 services will be displayed on
            the homepage.
          </p>
          <button
            className="btn btn-primary"
            onClick={() => {
              resetServiceForm();
              setShowServiceModal(true);
            }}
          >
            <i className="fas fa-plus me-2"></i>
            Add Service
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading-spinner">
          <i className="fas fa-spinner fa-spin"></i> Loading...
        </div>
      ) : (
        <div className="services-container">
          {services && services.length > 0 ? (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={
                  services && services.length > 0
                    ? services.map((service) => service.id)
                    : []
                }
                strategy={verticalListSortingStrategy}
              >
                <div className="services-list">
                  {services && services.length > 0 ? (
                    services.map((service, index) => (
                      <SortableServiceItem
                        key={service.id}
                        service={service}
                        index={index}
                      />
                    ))
                  ) : (
                    <div className="no-data">
                      <i className="fas fa-cogs"></i>
                      <p>
                        No services found. Click "Add Service" to get started.
                      </p>
                    </div>
                  )}
                </div>
              </SortableContext>
            </DndContext>
          ) : (
            <div className="no-data">
              <i className="fas fa-cogs"></i>
              <p>No services found. Click "Add Service" to get started.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );

  // Render contact content form
  const renderContactContent = () => (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        handleContentUpdate("contact", contactContent);
      }}
      className="content-form"
    >
      <div className="form-section">
        <h3>Contact Information</h3>
        <div className="form-grid">
          <div className="form-group">
            <label>Title</label>
            <input
              type="text"
              value={contactContent.title}
              onChange={(e) =>
                setContactContent({ ...contactContent, title: e.target.value })
              }
              placeholder="Contact page title"
            />
          </div>
          <div className="form-group">
            <label>Subtitle</label>
            <input
              type="text"
              value={contactContent.subtitle}
              onChange={(e) =>
                setContactContent({
                  ...contactContent,
                  subtitle: e.target.value,
                })
              }
              placeholder="Contact page subtitle"
            />
          </div>
          <div className="form-group full-width">
            <label>Address</label>
            <textarea
              value={contactContent.address}
              onChange={(e) =>
                setContactContent({
                  ...contactContent,
                  address: e.target.value,
                })
              }
              rows="3"
              placeholder="Company address"
            />
          </div>
          <div className="form-group">
            <label>Phone</label>
            <input
              type="text"
              value={contactContent.phone}
              onChange={(e) =>
                setContactContent({ ...contactContent, phone: e.target.value })
              }
              placeholder="Contact phone number"
            />
          </div>
          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              value={contactContent.email}
              onChange={(e) =>
                setContactContent({ ...contactContent, email: e.target.value })
              }
              placeholder="Contact email address"
            />
          </div>
          <div className="form-group full-width">
            <label>Office Hours</label>
            <textarea
              value={contactContent.office_hours}
              onChange={(e) =>
                setContactContent({
                  ...contactContent,
                  office_hours: e.target.value,
                })
              }
              rows="3"
              placeholder="Office hours information"
            />
          </div>
          <div className="form-group full-width">
            <label>Map Embed Code</label>
            <textarea
              value={contactContent.map_embed}
              onChange={(e) =>
                setContactContent({
                  ...contactContent,
                  map_embed: e.target.value,
                })
              }
              rows="4"
              placeholder="Google Maps embed code"
            />
          </div>
        </div>
      </div>

      <button type="submit" className="btn btn-primary" disabled={loading}>
        {loading ? (
          <i className="fas fa-spinner fa-spin me-2"></i>
        ) : (
          <i className="fas fa-save me-2"></i>
        )}
        Update Contact Content
      </button>
    </form>
  );

  return (
    <div className="content-manager">
      <div className="manager-header">
        <h2>Content Management</h2>
      </div>

      <div className="manager-content">
        {/* Section Navigation */}
        <div className="section-nav">
          {sections.map((section) => (
            <button
              key={section.id}
              className={`section-nav-item ${
                activeSection === section.id ? "active" : ""
              }`}
              onClick={() => setActiveSection(section.id)}
            >
              <i className={`${section.icon} me-2`}></i>
              {section.label}
            </button>
          ))}
        </div>

        {/* Content Area */}
        <div className="content-area">{renderSectionContent()}</div>
      </div>

      {/* Job Modal */}
      {showJobModal && (
        <div className="modal-overlay">
          <div className="modal-content large">
            <div className="modal-header">
              <h3>{editingJob ? "Edit Job Opening" : "Add Job Opening"}</h3>
              <button
                className="modal-close"
                onClick={() => {
                  setShowJobModal(false);
                  resetJobForm();
                }}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <form onSubmit={handleJobSubmit} className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label>Job Title *</label>
                  <input
                    type="text"
                    value={jobFormData.title}
                    onChange={(e) =>
                      setJobFormData({ ...jobFormData, title: e.target.value })
                    }
                    required
                    placeholder="Job title"
                  />
                </div>
                <div className="form-group">
                  <label>Department</label>
                  <input
                    type="text"
                    value={jobFormData.department}
                    onChange={(e) =>
                      setJobFormData({
                        ...jobFormData,
                        department: e.target.value,
                      })
                    }
                    placeholder="Department"
                  />
                </div>
                <div className="form-group">
                  <label>Location</label>
                  <input
                    type="text"
                    value={jobFormData.location}
                    onChange={(e) =>
                      setJobFormData({
                        ...jobFormData,
                        location: e.target.value,
                      })
                    }
                    placeholder="Job location"
                  />
                </div>
                <div className="form-group">
                  <label>Job Type</label>
                  <select
                    value={jobFormData.type}
                    onChange={(e) =>
                      setJobFormData({ ...jobFormData, type: e.target.value })
                    }
                  >
                    <option value="full-time">Full Time</option>
                    <option value="part-time">Part Time</option>
                    <option value="contract">Contract</option>
                    <option value="internship">Internship</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Experience Required</label>
                  <input
                    type="text"
                    value={jobFormData.experience}
                    onChange={(e) =>
                      setJobFormData({
                        ...jobFormData,
                        experience: e.target.value,
                      })
                    }
                    placeholder="e.g., 2-5 years"
                  />
                </div>
                <div className="form-group">
                  <label>Salary Range</label>
                  <input
                    type="text"
                    value={jobFormData.salary_range}
                    onChange={(e) =>
                      setJobFormData({
                        ...jobFormData,
                        salary_range: e.target.value,
                      })
                    }
                    placeholder="e.g., $50,000 - $70,000"
                  />
                </div>
                <div className="form-group full-width">
                  <label>Job Description</label>
                  <textarea
                    value={jobFormData.description}
                    onChange={(e) =>
                      setJobFormData({
                        ...jobFormData,
                        description: e.target.value,
                      })
                    }
                    rows="5"
                    placeholder="Detailed job description"
                  />
                </div>
                <div className="form-group full-width">
                  <label>Requirements</label>
                  <textarea
                    value={jobFormData.requirements}
                    onChange={(e) =>
                      setJobFormData({
                        ...jobFormData,
                        requirements: e.target.value,
                      })
                    }
                    rows="4"
                    placeholder="Job requirements and qualifications"
                  />
                </div>
                <div className="form-group full-width">
                  <label>Benefits</label>
                  <textarea
                    value={jobFormData.benefits}
                    onChange={(e) =>
                      setJobFormData({
                        ...jobFormData,
                        benefits: e.target.value,
                      })
                    }
                    rows="3"
                    placeholder="Employee benefits"
                  />
                </div>
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={jobFormData.is_active}
                      onChange={(e) =>
                        setJobFormData({
                          ...jobFormData,
                          is_active: e.target.checked,
                        })
                      }
                    />
                    <span>Active Job Opening</span>
                  </label>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowJobModal(false);
                    resetJobForm();
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? (
                    <i className="fas fa-spinner fa-spin me-2"></i>
                  ) : (
                    <i className="fas fa-save me-2"></i>
                  )}
                  {editingJob ? "Update Job" : "Create Job"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Infrastructure Modal */}
      {showInfrastructureModal && (
        <div className="modal-overlay">
          <div className="modal-content large">
            <div className="modal-header">
              <h3>
                {editingInfrastructure
                  ? "Edit Infrastructure Item"
                  : "Add Infrastructure Item"}
              </h3>
              <button
                className="modal-close"
                onClick={() => {
                  setShowInfrastructureModal(false);
                  resetInfrastructureForm();
                }}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <form onSubmit={handleInfrastructureSubmit} className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label>Title *</label>
                  <input
                    type="text"
                    value={infrastructureFormData.title}
                    onChange={(e) =>
                      setInfrastructureFormData({
                        ...infrastructureFormData,
                        title: e.target.value,
                      })
                    }
                    required
                    placeholder="Infrastructure title"
                  />
                </div>
                <div className="form-group">
                  <label>Icon (FontAwesome class)</label>
                  <input
                    type="text"
                    value={infrastructureFormData.icon}
                    onChange={(e) =>
                      setInfrastructureFormData({
                        ...infrastructureFormData,
                        icon: e.target.value,
                      })
                    }
                    placeholder="e.g., fas fa-industry"
                  />
                </div>
                <div className="form-group">
                  <label>Image URL</label>
                  <input
                    type="url"
                    value={infrastructureFormData.image_url}
                    onChange={(e) =>
                      setInfrastructureFormData({
                        ...infrastructureFormData,
                        image_url: e.target.value,
                      })
                    }
                    placeholder="Image URL"
                  />
                </div>
                <div className="form-group full-width">
                  <label>Description *</label>
                  <textarea
                    value={infrastructureFormData.description}
                    onChange={(e) =>
                      setInfrastructureFormData({
                        ...infrastructureFormData,
                        description: e.target.value,
                      })
                    }
                    rows="5"
                    required
                    placeholder="Infrastructure description"
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowInfrastructureModal(false);
                    resetInfrastructureForm();
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? (
                    <i className="fas fa-spinner fa-spin me-2"></i>
                  ) : (
                    <i className="fas fa-save me-2"></i>
                  )}
                  {editingInfrastructure ? "Update Item" : "Add Item"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Certification Modal */}
      {showCertificationModal && (
        <div className="modal-overlay">
          <div className="modal-content large">
            <div className="modal-header">
              <h3>
                {editingCertification
                  ? "Edit Certification"
                  : "Add Certification"}
              </h3>
              <button
                className="modal-close"
                onClick={() => {
                  setShowCertificationModal(false);
                  resetCertificationForm();
                }}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <form onSubmit={handleCertificationSubmit} className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label>Title *</label>
                  <input
                    type="text"
                    value={certificationFormData.title}
                    onChange={(e) =>
                      setCertificationFormData({
                        ...certificationFormData,
                        title: e.target.value,
                      })
                    }
                    required
                    placeholder="Certification title"
                  />
                </div>
                <div className="form-group">
                  <label>Certificate Number</label>
                  <input
                    type="text"
                    value={certificationFormData.cert_number}
                    onChange={(e) =>
                      setCertificationFormData({
                        ...certificationFormData,
                        cert_number: e.target.value,
                      })
                    }
                    placeholder="Certificate number"
                  />
                </div>
                <div className="form-group">
                  <label>Issuing Body</label>
                  <input
                    type="text"
                    value={certificationFormData.issuing_body}
                    onChange={(e) =>
                      setCertificationFormData({
                        ...certificationFormData,
                        issuing_body: e.target.value,
                      })
                    }
                    placeholder="Issuing organization"
                  />
                </div>
                <div className="form-group">
                  <label>Issued Date</label>
                  <input
                    type="date"
                    value={certificationFormData.issued_date}
                    onChange={(e) =>
                      setCertificationFormData({
                        ...certificationFormData,
                        issued_date: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="form-group">
                  <label>Expiry Date</label>
                  <input
                    type="date"
                    value={certificationFormData.expiry_date}
                    onChange={(e) =>
                      setCertificationFormData({
                        ...certificationFormData,
                        expiry_date: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="form-group">
                  <label>Icon (FontAwesome class)</label>
                  <input
                    type="text"
                    value={certificationFormData.icon}
                    onChange={(e) =>
                      setCertificationFormData({
                        ...certificationFormData,
                        icon: e.target.value,
                      })
                    }
                    placeholder="e.g., fas fa-certificate"
                  />
                </div>
                <div className="form-group">
                  <label>Image URL</label>
                  <input
                    type="url"
                    value={certificationFormData.image_url}
                    onChange={(e) =>
                      setCertificationFormData({
                        ...certificationFormData,
                        image_url: e.target.value,
                      })
                    }
                    placeholder="Image URL"
                  />
                </div>
                <div className="form-group full-width">
                  <label>Description *</label>
                  <textarea
                    value={certificationFormData.description}
                    onChange={(e) =>
                      setCertificationFormData({
                        ...certificationFormData,
                        description: e.target.value,
                      })
                    }
                    rows="5"
                    required
                    placeholder="Certification description"
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowCertificationModal(false);
                    resetCertificationForm();
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? (
                    <i className="fas fa-spinner fa-spin me-2"></i>
                  ) : (
                    <i className="fas fa-save me-2"></i>
                  )}
                  {editingCertification
                    ? "Update Certification"
                    : "Add Certification"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Service Modal */}
      {showServiceModal && (
        <div className="modal-overlay">
          <div className="modal-content large">
            <div className="modal-header">
              <h3>{editingService ? "Edit Service" : "Add Service"}</h3>
              <button
                className="modal-close"
                onClick={() => {
                  setShowServiceModal(false);
                  resetServiceForm();
                }}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <form onSubmit={handleServiceSubmit} className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label>Title *</label>
                  <input
                    type="text"
                    value={serviceFormData.title}
                    onChange={(e) =>
                      setServiceFormData({
                        ...serviceFormData,
                        title: e.target.value,
                      })
                    }
                    required
                    placeholder="Service title"
                  />
                </div>
                <div className="form-group">
                  <label>Icon (FontAwesome class)</label>
                  <input
                    type="text"
                    value={serviceFormData.icon}
                    onChange={(e) =>
                      setServiceFormData({
                        ...serviceFormData,
                        icon: e.target.value,
                      })
                    }
                    placeholder="e.g., fas fa-cogs"
                  />
                </div>
                <div className="form-group">
                  <label>Image URL</label>
                  <input
                    type="url"
                    value={serviceFormData.image_url}
                    onChange={(e) =>
                      setServiceFormData({
                        ...serviceFormData,
                        image_url: e.target.value,
                      })
                    }
                    placeholder="Service image URL"
                  />
                </div>
                <div className="form-group">
                  <label>Link URL</label>
                  <input
                    type="url"
                    value={serviceFormData.link_url}
                    onChange={(e) =>
                      setServiceFormData({
                        ...serviceFormData,
                        link_url: e.target.value,
                      })
                    }
                    placeholder="Service link URL"
                  />
                </div>
                <div className="form-group full-width">
                  <label>Description *</label>
                  <textarea
                    value={serviceFormData.description}
                    onChange={(e) =>
                      setServiceFormData({
                        ...serviceFormData,
                        description: e.target.value,
                      })
                    }
                    rows="4"
                    required
                    placeholder="Service description"
                  />
                </div>
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={serviceFormData.is_active}
                      onChange={(e) =>
                        setServiceFormData({
                          ...serviceFormData,
                          is_active: e.target.checked,
                        })
                      }
                    />
                    <span>Active Service</span>
                  </label>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowServiceModal(false);
                    resetServiceForm();
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? (
                    <i className="fas fa-spinner fa-spin me-2"></i>
                  ) : (
                    <i className="fas fa-save me-2"></i>
                  )}
                  {editingService ? "Update Service" : "Add Service"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Team Member Modal */}
      {showTeamModal && (
        <div className="modal-overlay">
          <div className="modal-content large">
            <div className="modal-header">
              <h3>
                {editingTeamMember ? "Edit Team Member" : "Add Team Member"}
              </h3>
              <button
                className="modal-close"
                onClick={() => {
                  setShowTeamModal(false);
                  resetTeamForm();
                }}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <form onSubmit={handleTeamMemberSubmit} className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label>Name *</label>
                  <input
                    type="text"
                    value={teamFormData.name}
                    onChange={(e) =>
                      setTeamFormData({ ...teamFormData, name: e.target.value })
                    }
                    required
                    placeholder="Full name"
                  />
                </div>
                <div className="form-group">
                  <label>Position *</label>
                  <input
                    type="text"
                    value={teamFormData.position}
                    onChange={(e) =>
                      setTeamFormData({
                        ...teamFormData,
                        position: e.target.value,
                      })
                    }
                    required
                    placeholder="Job position/title"
                  />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={teamFormData.email}
                    onChange={(e) =>
                      setTeamFormData({
                        ...teamFormData,
                        email: e.target.value,
                      })
                    }
                    placeholder="Email address"
                  />
                </div>
                <div className="form-group">
                  <label>LinkedIn URL</label>
                  <input
                    type="url"
                    value={teamFormData.linkedin_url}
                    onChange={(e) =>
                      setTeamFormData({
                        ...teamFormData,
                        linkedin_url: e.target.value,
                      })
                    }
                    placeholder="LinkedIn profile URL"
                  />
                </div>
                <div className="form-group">
                  <label>Image URL</label>
                  <input
                    type="url"
                    value={teamFormData.image_url}
                    onChange={(e) =>
                      setTeamFormData({
                        ...teamFormData,
                        image_url: e.target.value,
                      })
                    }
                    placeholder="Profile image URL"
                  />
                </div>
                <div className="form-group full-width">
                  <label>Description *</label>
                  <textarea
                    value={teamFormData.description}
                    onChange={(e) =>
                      setTeamFormData({
                        ...teamFormData,
                        description: e.target.value,
                      })
                    }
                    rows="5"
                    required
                    placeholder="Professional description and background"
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowTeamModal(false);
                    resetTeamForm();
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? (
                    <i className="fas fa-spinner fa-spin me-2"></i>
                  ) : (
                    <i className="fas fa-save me-2"></i>
                  )}
                  {editingTeamMember ? "Update Member" : "Add Member"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContentManager;
